import type {
  FocusEventHandler,
  InputHTMLAttributes,
  MouseEventHandler,
  ReactNode,
} from 'react'
import type { PasswordCharacterPatterns } from '@opallabs/principles'
import type { WithMultipleStyleOverrides, WithTheme } from '../../gem'

/**
 * @note This is a subset of all valid `type`s, with only the text-like
 *   options.
 */
export enum TextInputType {
  Date = 'date',
  Datetime = 'datetime-local',
  Email = 'email',
  Month = 'month',
  Number = 'number',
  Password = 'password',
  Search = 'search',
  TelephoneNumber = 'tel',
  Text = 'text',
  Time = 'time',
  Url = 'url',
  Week = 'week',
}

/**
 * If present and non-empty, forces the component into an error state even if
 * `validator` indicates the input is valid. Useful when submitting form data
 * that's rejected by the server.
 */
export type ExplicitInputError = string

export type TextInputValidationResult = Readonly<{
  errorMessage?: string
  valid: boolean
}>

export type TextInputProps = Readonly<
  WithMultipleStyleOverrides<'container' | 'input' | 'message'> &
    WithTheme &
    Partial<
      Pick<
        InputHTMLAttributes<HTMLInputElement>,
        | 'className'
        | 'disabled'
        | 'enterKeyHint'
        | 'maxLength'
        | 'minLength'
        | 'onClick'
        | 'onInput'
        | 'onKeyDown'
        | 'placeholder'
        | 'required'
      >
    > & {
      additionalAriaLabels?: {
        [key: string]: string | boolean | undefined
      }
      /**
       * @note It would be nice to have strong typing that constrained
       *   `autocompleteHint` based on `type`, as only certain combinations are
       *   allowed.
       * @see https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#attr-fe-autocomplete
       */
      autocompleteHint?: string
      clearable?: boolean
      /**
       * If `type` is `TextInputType.Search`, this is the HTML `id` attribute for
       * the element that displays this input’s search results. That element should
       * be marked as `aria-live="polite"`, so that screenreaders report updates.
       *
       * This prop should *not* be used with any `type` except
       * `TextInputType.Search`.
       *
       * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-controls
       * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-live
       */
      controlsHtmlId?: string
      explicitError?: ExplicitInputError
      /**
       * Force validation to run even if the user has not modified `value`.
       *
       * @note Before using this try setting `previouslyModified=true`. It’ll probably achieve the result you want, and
       * avoids `forceValidation`’s risk of triggering an infinite loop.
       */
      forceValidation?: boolean
      htmlId?: string
      helpText?: string
      initialValue?: string
      leadingIconGlyph?: string
      maxValue?: number | string
      minValue?: number | string
      onValidate?: (valid: boolean) => void
      passwordCharacterPatterns?: PasswordCharacterPatterns
      /**
       * Override internal state. Useful for components that wrap `<TextInput>` and
       * manage state at a higher level.
       */
      previouslyModified?: boolean
      onChange: (value: string) => void
      onContainerMouseOut?: MouseEventHandler<HTMLDivElement>
      onContainerMouseOver?: MouseEventHandler<HTMLDivElement>
      onInputBlur?: FocusEventHandler<HTMLInputElement>
      onInputFocus?: FocusEventHandler<HTMLInputElement>
      trailingIconGlyph?: string
      trailingNode?: ReactNode
      type?: TextInputType
      value?: string
      validator?: (value: string) => TextInputValidationResult
    }
>

export type StyleProps = Readonly<
  Pick<TextInputProps, 'disabled'> &
    Required<Pick<TextInputProps, 'theme'>> & {
      leadingIcon?: boolean
      modified?: boolean
      trailingButton?: boolean
    }
>
